/**
 * "How this bills" (F6, F8, D86).
 *
 * A quote bills the quote. Otherwise the job is a service call (the fee, then
 * labour per 15 minutes to the nearest, then travel past the included km), a
 * contractor's job at the contractor's rates, or plain time and materials. The
 * breakdown is the phone's own arithmetic (`servicecall.forJob`) over what the
 * device holds, with a running on-site stretch counted so it moves live; the
 * server's invoice is the authority.
 */
import { useEffect, useRef, useState } from "react";

import * as actions from "../lib/actions";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { money } from "../lib/format";
import { useLocal } from "../lib/local";
import { withRunningMinutes } from "../lib/onsite";
import * as pricing from "../lib/pricing";
import * as repo from "../lib/repo";
import * as servicecall from "../lib/servicecall";
import * as store from "../lib/store";
import type { JobDetailOut } from "../lib/types";
import { AdjustmentEditor, type Adjustment } from "./AdjustmentEditor";
import { useNow } from "./OnSiteClock";
import { Card } from "./ui";

const MODES = [
  ["service_call", "Service call"],
  ["contractor", "Contractor"],
  ["time_and_materials", "Time & materials"],
] as const;

const MAX_KM = 5000;

function kmText(km: number): string {
  return km.toFixed(1).replace(/\.0$/, "");
}

/** A typed distance: a number from 0 to MAX_KM, or null when it isn't one. */
export function parseKm(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 && n <= MAX_KM ? n : null;
}

export function BillingCard({ job, onChanged }: { job: JobDetailOut; onChanged: () => void }) {
  const { can } = useAuth();
  const { data: rows, reload: reloadRows } = useLocal(async () => {
    const [org, customer, site] = await Promise.all([
      repo.org(),
      store.get<store.Row>("customer", job.customer_id),
      job.site_id ? store.get<store.Row>("site", job.site_id) : Promise.resolve(undefined),
    ]);
    return { org, customer: customer ?? null, site: site ?? null };
  }, [job.id, job.customer_id, job.site_id, job.updated_at]);

  const entries = job.time_entries ?? [];
  const now = useNow(entries.some((e) => !e.ended_at && e.kind === "on_site"));
  const [km, setKm] = useState("");
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: "info" | "crit"; text: string } | null>(null);
  const note = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    note.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [said]);

  const quoted = job.quoted_cents > 0;
  const mode = MODES.some(([value]) => value === job.billing_mode)
    ? (job.billing_mode as string)
    : "time_and_materials";
  const customer = rows?.customer ?? null;
  const site = rows?.site ?? null;
  const isContractor = customer?.is_contractor === true;
  const customerName = String(customer?.name ?? "the customer");

  async function save(fields: { billing_mode?: string; travel_km_one_way?: number | null }, failed: string) {
    setBusy(true);
    setSaid(null);
    try {
      const { queued, message } = await actions.setBilling(job.id, fields);
      if (queued && message) setSaid({ tone: "info", text: message });
      onChanged();
      reloadRows();
    } catch (err) {
      setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : failed });
    } finally {
      setBusy(false);
    }
  }

  async function saveKm() {
    const value = parseKm(km);
    if (value === null) {
      setSaid({ tone: "crit", text: `Distance has to be a number of km from 0 to ${MAX_KM}.` });
      return;
    }
    await save({ travel_km_one_way: value }, "Couldn't save the distance.");
    setKm("");
  }

  /** Ask the server to work the distance out from the two addresses. Needs a signal. */
  async function workItOut() {
    if (!job.site_id) return;
    setBusy(true);
    setSaid(null);
    try {
      const { site: answer, note } = await api.sites.distance(job.site_id);
      // Keep the device's copy in step, as the server's answer is the truth.
      await store.patch("site", job.site_id, {
        distance_km: answer.distance_km ?? null,
        distance_source: answer.distance_source ?? null,
        distance_checked_at: answer.distance_checked_at ?? null,
      });
      if (note) setSaid({ tone: "info", text: note });
      onChanged();
      reloadRows();
    } catch (err) {
      setSaid({
        tone: "crit",
        text:
          err instanceof ApiError && err.status !== 0
            ? err.message
            : "Working out the distance needs a signal. Type the km in instead.",
      });
    } finally {
      setBusy(false);
    }
  }

  // The office sets a discount or buffer until the job is billed; the server
  // applies it to a quoted job too.
  async function saveAdjustment(next: Adjustment) {
    await actions.setAdjustment(job.id, next);
    onChanged();
  }
  // Owner and office only — a pricing decision, not a supervisor's (round 12).
  const editor = can("owner", "office") && job.status !== "invoiced" && (
    <AdjustmentEditor
      bp={job.adjustment_bp}
      cents={job.adjustment_cents}
      label={job.adjustment_label}
      idPrefix="job-adjustment"
      onSave={saveAdjustment}
    />
  );

  if (quoted) {
    return (
      <Card title="How this bills" subtitle="Quoted">
        <p className="tiny">Quoted — bills the quote ({money(job.quoted_cents)}) plus approved variations.</p>
        {editor}
      </Card>
    );
  }

  const serviceMode = mode === "service_call" || mode === "contractor";
  const lines =
    serviceMode && rows
      ? servicecall.forJob({ ...job }, withRunningMinutes(entries, now), rows.org, customer, site)
      : null;

  // A discount or buffer comes off the whole bill, parts included (D87), so it
  // is said here rather than folded into a total that is the visit only.
  const hasAdjustment = Boolean(job.adjustment_bp) || (job.adjustment_cents ?? null) !== null;
  const adjustmentWords = hasAdjustment
    ? `${pricing.adjustmentLabel(job.adjustment_bp, job.adjustment_cents, job.adjustment_label)}${
        job.adjustment_cents !== null && job.adjustment_cents !== undefined ? ` (${money(job.adjustment_cents)})` : ""
      } comes off the whole bill, parts included — Live margin and the invoice show the amount.`
    : null;

  const siteKm = site?.distance_km === null || site?.distance_km === undefined ? null : Number(site.distance_km);
  const distance =
    job.travel_km_one_way !== null && job.travel_km_one_way !== undefined
      ? `${kmText(job.travel_km_one_way)} km one way (entered for this job)`
      : siteKm !== null
        ? `${kmText(siteKm)} km one way (${site?.distance_source === "entered" ? "entered" : "calculated"})`
        : "unknown";

  return (
    <Card title="How this bills" subtitle={MODES.find(([value]) => value === mode)?.[1]}>
      <label className="bill-field">
        <span className="tiny muted">Bills as</span>
        <select
          value={mode}
          disabled={busy}
          aria-label="How this job bills"
          onChange={(e) => void save({ billing_mode: e.target.value }, "Couldn't change how it bills.")}
        >
          {MODES.map(([value, label]) => (
            <option key={value} value={value} disabled={value === "contractor" && !isContractor && mode !== value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      {isContractor && mode !== "contractor" && (
        <p className="tiny muted" style={{ marginTop: 8 }}>
          {customerName} is a contractor — choose Contractor to bill at their rates.
        </p>
      )}
      {mode === "contractor" && (
        <p className="tiny muted" style={{ marginTop: 8 }}>
          At {customerName}&rsquo;s rates.
        </p>
      )}

      {serviceMode && (
        <>
          <p className="tiny" style={{ marginTop: 12 }}>
            Distance: {distance}
          </p>
          <div className="bill-km">
            <input
              type="number"
              inputMode="decimal"
              min={0}
              max={MAX_KM}
              step="0.1"
              value={km}
              placeholder="km one way"
              aria-label="km one way for this job"
              onChange={(e) => setKm(e.target.value)}
            />
            <button type="button" className="link-button bill-action" disabled={busy} onClick={() => void saveKm()}>
              Save
            </button>
            <button
              type="button"
              className="link-button bill-action"
              disabled={busy || job.travel_km_one_way === null || job.travel_km_one_way === undefined}
              onClick={() => void save({ travel_km_one_way: null }, "Couldn't clear the distance.")}
            >
              Clear
            </button>
            <button type="button" className="link-button bill-action" disabled={busy} onClick={() => void workItOut()}>
              Work it out
            </button>
          </div>

          <h3 className="bill-head">This visit so far</h3>
          {lines === null ? null : lines.length === 0 ? (
            <p className="tiny muted">Service-call rates aren&rsquo;t set up — Settings → Business.</p>
          ) : (
            <dl className="kv kv-money">
              {lines.map((line) => (
                <BillLine key={line.key} description={line.description} cents={servicecall.lineTotal(line)} />
              ))}
              <dt className="total">Total ex GST</dt>
              <dd className="mono total">{money(servicecall.totalCents(lines))}</dd>
            </dl>
          )}
          <p className="tiny muted" style={{ marginTop: 8 }}>
            plus parts
          </p>
        </>
      )}

      {adjustmentWords && (
        <p className="tiny" style={{ marginTop: 8 }}>
          {adjustmentWords}
        </p>
      )}
      {editor}

      {said && (
        <p
          ref={note}
          className={`notice notice-${said.tone}`}
          role={said.tone === "crit" ? "alert" : "status"}
          style={{ marginTop: 10 }}
        >
          {said.text}
        </p>
      )}
    </Card>
  );
}

function BillLine({ description, cents }: { description: string; cents: number }) {
  return (
    <>
      <dt>{description}</dt>
      <dd className="mono">{money(cents)}</dd>
    </>
  );
}
